import { DateTime } from 'luxon';
import { z } from 'zod';

import { FF_WOM_PRICING } from '@/constants/featureFlags';
import ffDefaults from '@/constants/ffDefaults';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';

const DEFAULT_PRICING = ffDefaults[FF_WOM_PRICING];

const amountSchema = z.number().finite().nonnegative();
// Requires a timezone offset so the window doesn't shift with the viewer's timezone
const dateSchema = z
  .string()
  .datetime({ offset: true })
  .transform((value) => DateTime.fromISO(value, { setZone: true }));

const copyVariantSchema = z.enum(['standard', 'increased']);

export type WomCopyVariant = z.infer<typeof copyVariantSchema>;

const overrideSchema = z
  .object({
    discount: amountSchema,
    startDate: dateSchema,
    endDate: dateSchema,
    copyVariant: copyVariantSchema.catch('standard'),
  })
  .refine(({ startDate, endDate }) => endDate > startDate);

type WomPricingOverride = z.infer<typeof overrideSchema>;

export type WomPricing = {
  /** Discount currently on offer: an active override's, otherwise the base discount */
  discount: number;
  baseDiscount: number;
  consultPrice: number;
  /** Never below 0, even when the discount exceeds the consult price */
  priceAfterDiscount: number;
  /** Price once overrides end, also never below 0 */
  basePriceAfterDiscount: number;
  isOverrideActive: boolean;
  /** "increased" only when the active override asks for it and its discount is above the base, otherwise "standard" */
  copyVariant: WomCopyVariant;
};

const readAmount = (value: unknown, fallback: number) => {
  const result = amountSchema.safeParse(value);
  return result.success ? result.data : fallback;
};

// Invalid overrides (missing or bad dates, end not after start, bad discount) are dropped so the base discount is used
const readOverrides = (value: unknown): WomPricingOverride[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.flatMap((override) => {
    const result = overrideSchema.safeParse(override);
    return result.success ? [result.data] : [];
  });
};

// The first override whose window contains `now` (startDate inclusive, endDate exclusive) wins
export const resolveWomPricing = (flagValue: unknown, now: DateTime): WomPricing => {
  const config = z.record(z.unknown()).safeParse(flagValue);
  const { discount, consultPrice, overrides } = config.success ? config.data : {};

  const baseDiscount = readAmount(discount, DEFAULT_PRICING.discount);
  const resolvedConsultPrice = readAmount(consultPrice, DEFAULT_PRICING.consultPrice);
  const activeOverride = readOverrides(overrides).find(({ startDate, endDate }) => startDate <= now && now < endDate);
  const activeDiscount = activeOverride?.discount ?? baseDiscount;

  return {
    discount: activeDiscount,
    baseDiscount,
    consultPrice: resolvedConsultPrice,
    priceAfterDiscount: Math.max(resolvedConsultPrice - activeDiscount, 0),
    basePriceAfterDiscount: Math.max(resolvedConsultPrice - baseDiscount, 0),
    isOverrideActive: Boolean(activeOverride),
    // "Increased from X to Y" only reads correctly when the override discount is above the base
    copyVariant:
      activeOverride?.copyVariant === 'increased' && activeDiscount > baseDiscount ? 'increased' : 'standard',
  };
};

export const useWomPricing = (): WomPricing => {
  const flagValue = useFeatureFlag(FF_WOM_PRICING);
  return resolveWomPricing(flagValue, DateTime.now());
};
